import { BaseButton } from 'components/ui/BaseButton';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { MembershipSetItem, MembershipTypeCodeType } from 'types/api';
import { priceFormat } from 'utils/priceFormat';
import dayjs from 'dayjs';
import { MembershipBadge } from 'components/membership/MembershipBadge';
import { useRecoilValue } from 'recoil';
import { shopDetailState } from 'store/atoms';
import { useTranslation } from 'i18n';

const CardListContainer = styled.div<{ $showSingleCard?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MembershipCard = styled.div`
  background-color: #ffffff;
  border-radius: 4px;
  padding: 20px;
  border: 1px solid #eaebef;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MembershipTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #0f0f0f;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const MembershipDate = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #0f0f0f;
`;

const SessionsLeft = styled.div<{ $isActive: boolean }>`
  font-size: 15px;
  color: ${({ $isActive }) => ($isActive ? '#006300' : '#B6BCBF')};
  font-weight: 600;
  line-height: 20px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: auto;
`;

const TotalCountText = styled.span`
  color: #b6bcbf;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

const DivideLine = styled.span`
  margin: 0 6px 0 6px;
`;

interface MyMembershipCardListProps {
  memberships: any;
  showSingleCard?: boolean;
  onCardClick?: (membershipId: number) => void;
}

export const MyMembershipCardList = ({
  memberships,
  showSingleCard = false,
  onCardClick,
}: MyMembershipCardListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shopDetail = useRecoilValue(shopDetailState);
  const currencyConfig = shopDetail?.solutionConfig?.global_config?.currencyConfig;

  const handleCardClick = (membershipId: number) => {
    if (onCardClick) {
      onCardClick(membershipId);
    }
  };

  const handleBookTime = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigate('/booking');
  };

  const getPrimaryMembership = (membershipData: any) => {
    const membershipList = membershipData.memberships;

    if (!membershipList || membershipList.length === 0) {
      return null;
    }

    if (membershipList.length === 1) {
      return membershipList[0];
    }

    const practiceMembership = membershipList.find(
      (membership: MembershipSetItem) => membership.membershipCategoryCd === 'PRACTICE',
    );

    return practiceMembership || membershipList[0];
  };

  const getMembershipTypeDisplay = (
    membershipTypeCd: MembershipTypeCodeType,
    membership: MembershipSetItem,
  ) => {
    if (membershipTypeCd === 'COUNT') {
      return (
        <>
          {t('my_booking.detail.sessions_left', { count: membership.membershipBalanceCnt })}
          <TotalCountText>
            {' '}
            <DivideLine>| </DivideLine>
            {t('my_membership.detail.total_sessions', { count: membership.membershipTotalCnt })}
          </TotalCountText>
        </>
      );
    }

    if (membershipTypeCd === 'BALANCE') {
      return (
        <>
          {priceFormat(membership.membershipBalanceAmt, currencyConfig)}
          <TotalCountText>
            {' '}
            <DivideLine>| </DivideLine> {priceFormat(membership.membershipTotalAmt, currencyConfig)}
          </TotalCountText>
        </>
      );
    }

    if (membershipTypeCd === 'PERIOD') {
      return t('my_booking.detail.until', {
        date: dayjs(membership.membershipExpiredDate).format('MMM DD, YYYY'),
      });
    }

    return '';
  };

  const displayMemberships = showSingleCard ? memberships.slice(0, 1) : memberships;

  return (
    <CardListContainer $showSingleCard={showSingleCard}>
      {displayMemberships.map((membership: any) => {
        const primaryMembership = getPrimaryMembership(membership);

        return (
          <MembershipCard
            key={membership.membershipSetId}
            onClick={() => handleCardClick(primaryMembership.membershipSetId)}
          >
            <CardHeader>
              <MembershipBadge
                isActive={membership.membershipSetStatusCd === 'ACTIVE'}
                text={
                  primaryMembership
                    ? primaryMembership.membershipTypeCd
                    : membership.membershipSetCategoryCd
                }
              ></MembershipBadge>
            </CardHeader>

            <div>
              <MembershipTitle>{membership.memberships[0].membershipName}</MembershipTitle>
              <MembershipDate>
                {dayjs(membership.membershipSetStartedDate).format('MMM DD, YYYY')} ~{' '}
                {dayjs(membership.membershipSetExpiredDate).format('MMM DD, YYYY')}
              </MembershipDate>
            </div>
            {primaryMembership && (
              <SessionsLeft $isActive={membership.membershipSetStatusCd === 'ACTIVE'}>
                {getMembershipTypeDisplay(primaryMembership.membershipTypeCd, primaryMembership)}
              </SessionsLeft>
            )}

            {membership.membershipSetStatusCd === 'ACTIVE' && (
              <ButtonContainer>
                <BaseButton
                  text={t('my_booking.cta.book_tee_time')}
                  variant="outline"
                  size="medium"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleBookTime(e)}
                />
              </ButtonContainer>
            )}
          </MembershipCard>
        );
      })}
    </CardListContainer>
  );
};
